/**
 * Utilidades para validación de tokens JWT
 */

/**
 * Valida si un token JWT tiene formato válido
 * @param token Token a validar
 * @returns true si el token tiene formato válido (3 partes separadas por punto)
 */
export function isTokenFormatValid(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  const parts = token.split('.');
  return parts.length === 3;
}

/**
 * Decodifica el payload de un token JWT (sin verificar firma)
 * @param token Token JWT
 * @returns Payload decodificado o null si es inválido
 */
export function decodeTokenPayload(token: string): any | null {
  if (!isTokenFormatValid(token)) {
    return null;
  }

  try {
    const parts = token.split('.');
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Verifica si un token JWT está expirado
 * @param token Token JWT
 * @returns true si el token está expirado o es inválido
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeTokenPayload(token);
  
  if (!payload || !payload.exp) {
    return true; // Si no tiene exp, considerar expirado
  }

  const expirationTime = payload.exp * 1000; // Convertir a milisegundos
  const currentTime = Date.now();
  
  return currentTime >= expirationTime;
}

/**
 * Valida completamente un token JWT
 * @param token Token a validar
 * @returns true si el token es válido (formato correcto y no expirado)
 */
export function isTokenValid(token: string): boolean {
  if (!token) {
    return false;
  }

  if (!isTokenFormatValid(token)) {
    return false;
  }

  if (isTokenExpired(token)) {
    return false;
  }

  return true;
}

/**
 * Obtiene el tiempo restante hasta la expiración del token
 * @param token Token JWT
 * @returns Tiempo restante en milisegundos, o null si es inválido o expirado
 */
export function getTokenTimeRemaining(token: string): number | null {
  const payload = decodeTokenPayload(token);
  
  if (!payload || !payload.exp) {
    return null;
  }

  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const remaining = expirationTime - currentTime;
  
  return remaining > 0 ? remaining : null;
}
